import React, {ChangeEvent, ReactNode, useState} from 'react';
import {SubMenuProps} from './SubMenu';
import {CaptionIcon, CrossIcon, MagicStick} from '../../../ui-components/Iconpack';
import {CommonSpinLoader} from '../../../ui-components/loaders';
import {generateCaption, generateTags} from '../quickTrial';
import {Caption, Tag} from '../../../reducers/media';
import {toast} from 'react-toastify';

export const AnnotationSubMenu: React.FC<SubMenuProps> = (props) => {
    const [captionLoading, setCaptionLoading] = useState<boolean>(false);
    const [tagsLoading, setTagsLoading] = useState<boolean>(false);
    const [newTag, setNewTag] = useState<string>('');

    const {caption, tags} = props.transformationObject;

    const handleGenerateTags = () => {
        props.trackEvent('GenerateTagsClicked', {});
        if (props.fileData?.uid && props.orgUid) {
            setTagsLoading(true);
            generateTags(props.fileData?.uid, props.orgUid)
                .then((res) => {
                    if (res.data) {
                        const generatedTags: Tag[] = res.data['tags'] ?? [];
                        if (generatedTags.length > 0) {
                            props.updateTransformationObject({
                                ...props.transformationObject,
                                tags: generatedTags.map((tag) => tag.text),
                            });
                        } else {
                            toast.error('Could not generate tags for the current image.');
                        }
                    } else if (res.error) {
                        toast.error(res.error);
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => setTagsLoading(false));
        }
    };

    const handleGenerateCaption = () => {
        props.trackEvent('GenerateCaptionClicked', {});
        if (props.fileData?.uid && props.orgUid) {
            setCaptionLoading(true);
            generateCaption(props.fileData?.uid, props.orgUid)
                .then((res) => {
                    if (res.data) {
                        const generatedCaptions: Caption[] = res.data['captions'] ?? [];
                        if (generatedCaptions.length > 0) {
                            props.updateTransformationObject({
                                ...props.transformationObject,
                                caption: generatedCaptions[0].text,
                            });
                        } else {
                            toast.error('Could not generate caption for the current image');
                        }
                    } else if (res.error) {
                        toast.error(res.error);
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    setCaptionLoading(false);
                });
        }
    };

    const handleAddTag = () => {
        props.trackEvent('AddTagClicked', {});
        if (newTag !== '' && !tags.includes(newTag)) {
            const updatedTags = [...tags, newTag];
            props.updateTransformationObject({...props.transformationObject, tags: updatedTags});
        }
        setNewTag('');
    };

    const handleRemoveTag = (tag: string) => {
        props.trackEvent('RemoveTagClicked', {});
        const updatedTags = tags.filter((t) => t !== tag);
        props.updateTransformationObject({...props.transformationObject, tags: updatedTags});
    };

    const Btn = (props: {onClick: () => void; children?: ReactNode}) => {
        return (
            <div
                className='border-1 text-inter flex cursor-pointer justify-center rounded-2xl bg-[#FFF] px-2 py-3 text-[14px] font-[700] text-[#1570EF]'
                onClick={props.onClick}>
                {props.children}
            </div>
        );
    };

    const AnnotationGenerateBtn = (props: {text: string; onGenerate: () => void}) => {
        return (
            <Btn onClick={props.onGenerate}>
                <span className={'px-3'}>{props.text}</span>
                <MagicStick />
            </Btn>
        );
    };

    return (
        <div className={'flex flex-col gap-10'}>
            <div>
                <div className='flex items-center'>
                    <div className='block cursor-pointer items-center justify-center rounded-[6px] border-[2px] border-[#4E5BA6] bg-[#717BBC] p-[12px] md:mx-[16px] md:hidden'>
                        <CaptionIcon color='#FFFFFF' />
                    </div>
                    <div className='flex flex-col pl-[6px] text-[14px] font-[700] text-[#FFFFFF] md:pl-0'>
                        <span>CAPTION</span>
                    </div>
                </div>
                {/*captions*/}
                <div className='my-4 rounded-2xl bg-[#363F72] p-2'>
                    <div className='flex items-center justify-center'>
                        {captionLoading ? (
                            <CommonSpinLoader className={'h-[95px]'} size={'sm'} />
                        ) : (
                            <textarea
                                className='text-inter w-full resize-none rounded border-none bg-[#363F72] px-2 py-1 text-[14px] text-[#FFF] outline-none'
                                placeholder={'enter caption...'}
                                value={caption ?? ''}
                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                    props.updateTransformationObject({
                                        ...props.transformationObject,
                                        caption: event.target.value,
                                    });
                                }}
                                rows={4}
                            />
                        )}
                    </div>
                </div>

                {/*generate captions*/}
                {!caption && props.fileData && (
                    <AnnotationGenerateBtn text={'Generate Caption'} onGenerate={handleGenerateCaption} />
                )}
            </div>

            {/*tags*/}

            <div>
                <span className='text-inter text-[14px] font-[700] text-[#FFF]'>TAGS</span>
                <div className='my-4 rounded-2xl bg-[#363F72] p-2'>
                    <div className='flex items-center justify-center'>
                        {tagsLoading ? (
                            <CommonSpinLoader className={'h-[95px]'} size={'sm'} />
                        ) : (
                            <div className='flex max-h-[400px] flex-wrap gap-1 overflow-y-auto rounded-[16px] bg-[#363F72] p-1'>
                                {tags.map((tag, index) => (
                                    <div
                                        key={index}
                                        className='flex items-center rounded-[12px] bg-[#293056] p-2 font-[400] text-white'>
                                        <span className='mr-1'>{tag}</span>
                                        <button onClick={() => handleRemoveTag(tag)} className='text-white'>
                                            <CrossIcon />
                                        </button>
                                    </div>
                                ))}
                                <div className='flex items-center'>
                                    <input
                                        type='text'
                                        className='text-inter overflow-y-auto rounded border-none bg-[#363F72] px-2 py-1 text-[14px] text-[#FFF] outline-none'
                                        placeholder='+ new tag'
                                        value={newTag}
                                        onChange={(e) => setNewTag(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/*generate tags*/}
                {tags.length === 0 && props.fileData && (
                    <AnnotationGenerateBtn text={'Generate Tags'} onGenerate={handleGenerateTags} />
                )}
            </div>
        </div>
    );
};
